import type React from 'react';
import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Card, Heading, TextButton, VisuallyHidden } from '@zillow/constellation-10';
import { IconChevronDownOutline, IconChevronUpOutline } from '@zillow/constellation-icons';

import MarketPreview from './MarketPreview';
import FilterTray from 'app/shared/components/FilterTray';
import { useMarketTrendsFilters } from 'app/shared/hooks/useFilters';

import type { State } from 'app/shared/types/State';
import type { Market } from 'app/shared/types/Market';

interface MarketPreviewsProps {
  markets: Array<Market>;
  heading: string;
  subject: string;
  useFilters?: boolean;
  isSmallScreen: boolean;
  isMobile: boolean;
  emitAnalyticsEvent?: () => void;
}

const MarketPreviews: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<MarketPreviewsProps>>> = (
  props,
) => {
  const { heading, subject, useFilters = false, isSmallScreen, isMobile, emitAnalyticsEvent } = props;
  const dispatch = useDispatch();
  const filter = useSelector((state: State) => state.filter);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const expand = useCallback(() => setIsExpanded(!isExpanded), [isExpanded, setIsExpanded]);
  const { bedrooms, setBedrooms, propertyTypes, setPropertyTypes } = useMarketTrendsFilters(filter, dispatch);

  const markets = props.markets.filter((market) => market.medianRent !== 0); // dont include when theres no data
  const filterDisplay = (
    <FilterTray
      bedrooms={bedrooms}
      setBedrooms={setBedrooms}
      propertyTypes={propertyTypes}
      setPropertyTypes={setPropertyTypes}
      loading={loading}
      setLoading={setLoading}
      isMobile={isMobile}
      style={isSmallScreen ? {} : { marginRight: 60 }}
    />
  );
  return (
    <Card
      tone="neutral"
      css={{
        padding: '600',
        paddingInline: isSmallScreen ? undefined : '600',
        marginTop: '600',
      }}
    >
      <Heading level={2} textStyle="heading-sm" fontFamily="heading-alt">
        {heading}
      </Heading>
      {useFilters &&
        (isSmallScreen ? (
          filterDisplay
        ) : (
          <Box css={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            {filterDisplay}
          </Box>
        ))}

      <Box
        css={{
          display: 'grid',
          gap: isSmallScreen ? '200' : '800',
          marginBottom: '800',
          // the filters act as a spacer, so need to add margin when the filters are not present
          marginTop: useFilters ? undefined : '600',
          gridAutoFlow: 'row',
          paddingRight: isSmallScreen ? '' : '1500',
          gridTemplateColumns: isSmallScreen ? `repeat(auto-fill, 160px)` : `repeat(auto-fill, minmax(170px, 1fr))`,
        }}
      >
        {markets.map((market, i) => {
          // only show first 4 top markets on mobile
          if (isSmallScreen && i > 3 && !isExpanded) {
            return (
              <VisuallyHidden key={`${market.name}-hidden`}>
                <MarketPreview {...market} isSmallScreen={isSmallScreen} emitAnalyticsEvent={emitAnalyticsEvent} />
              </VisuallyHidden>
            );
          }
          return (
            <MarketPreview
              {...market}
              isSmallScreen={isSmallScreen}
              key={market.name}
              emitAnalyticsEvent={emitAnalyticsEvent}
            />
          );
        })}
      </Box>
      {markets.length > 4 && isSmallScreen && (
        <TextButton
          onClick={expand}
          icon={isExpanded ? <IconChevronUpOutline /> : <IconChevronDownOutline />}
          iconPosition="end"
        >
          {isExpanded ? `See less ${subject}` : `See more ${subject}`}
        </TextButton>
      )}
    </Card>
  );
};

export default MarketPreviews;
